import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface MiniPlayerState {
  title: string;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isVideo: boolean;
}

/**
 * Keeps an audio OR video file playing in a small floating player that outlives the
 * preview dialog and survives CRM route navigation. The service owns a single media
 * element (audio bar or a video window), created lazily, so playback continues even
 * after the dialog component is destroyed. The preview components hand off here on
 * "minimize"; the mini-player component renders the UI (a bar for audio, a small video
 * window for video) and can re-open the full dialog from `model` + the live position.
 */
@Injectable({ providedIn: 'root' })
export class AudioMiniPlayerService {
  /** The persistent element — audio or video, created on first minimize. */
  element: HTMLMediaElement | null = null;
  isVideo = false;
  /** Original dialog data, so the full player can be re-opened with full context. */
  model: any = null;
  private active = false;
  private title = '';

  readonly state$ = new BehaviorSubject<MiniPlayerState | null>(null);

  /** Hand off from the full player: continue from `currentTime`/`isPlaying`. */
  open(model: any, url: string, title: string, currentTime: number, isPlaying: boolean, isVideo = false): void {
    // A type switch (audio<->video) needs a fresh element.
    if (this.element && this.isVideo !== isVideo) {
      this.element.pause();
      this.element = null;
    }
    this.model = model;
    this.title = title;
    this.isVideo = isVideo;
    this.active = true;

    if (!this.element) {
      const el: HTMLMediaElement = isVideo ? document.createElement('video') : new Audio();
      if (isVideo) { (el as HTMLVideoElement).playsInline = true; el.setAttribute('playsinline', ''); }
      el.addEventListener('timeupdate', () => this.push());
      el.addEventListener('play', () => this.push());
      el.addEventListener('pause', () => this.push());
      el.addEventListener('loadedmetadata', () => this.push());
      el.addEventListener('ended', () => this.push());
      this.element = el;
    }
    const a = this.element;
    if (a.src !== url) a.src = url;

    const start = () => {
      try { a.currentTime = currentTime || 0; } catch { /* not seekable yet */ }
      if (isPlaying) a.play().catch(() => {});
      this.push();
    };
    if (a.readyState >= 1) start();
    else a.addEventListener('loadedmetadata', start, { once: true });
    this.push();
  }

  togglePlay(): void {
    const a = this.element;
    if (!a) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  }

  seek(time: number): void {
    if (this.element) { try { this.element.currentTime = time; } catch {} this.push(); }
  }

  /** Stop and hide the mini-player entirely. */
  stop(): void {
    this.active = false;   // set BEFORE pause(), so the async pause event can't re-show it
    if (this.element) { this.element.pause(); this.element.removeAttribute('src'); this.element.load(); }
    this.model = null;
    this.state$.next(null);
  }

  /** Hide the mini-player UI but keep the current position/state for a re-open. */
  hideForReopen(): { model: any; currentTime: number; isPlaying: boolean; isVideo: boolean } {
    const payload = {
      model: this.model,
      currentTime: this.element?.currentTime || 0,
      isPlaying: !!this.element && !this.element.paused,
      isVideo: this.isVideo,
    };
    this.active = false;
    if (this.element) this.element.pause();
    this.state$.next(null);
    return payload;
  }

  private push(): void {
    const a = this.element;
    if (!a || !this.active) return;
    this.state$.next({
      title: this.title || (this.isVideo ? 'Video' : 'Audio'),
      isPlaying: !a.paused,
      currentTime: a.currentTime || 0,
      duration: isFinite(a.duration) ? a.duration : 0,
      isVideo: this.isVideo,
    });
  }
}
